import { useParams, useLocation } from "react-router-dom";
import { useMemo, useState, useCallback, useEffect } from "react";
import { v4 as uuid } from "uuid";

import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";

const TAX_RATE = 0.1;
const CURRENCY = "KRW";

export default function In_new() {
  const { id: projectId, dns } = useParams();
  const location = useLocation();

  // 🔥 발주서에서 넘어온 데이터
  const poHeader = location.state?.poHeader;
  const poItems = location.state?.poItems;
  const fromDns = location.state?.from;

  // ----------------------------
  // Header 상태
  // ----------------------------
  const [title, setTitle] = useState("자재 입고서(공식)");
  const [inDate, setInDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState("");
  const [remark, setremark] = useState("");

  // ----------------------------
  // Row 구조 (⚠️ 반드시 위에 있어야 함)
  // ----------------------------
  const makeRow = () => ({
    _id: uuid(),
    code: "",
    name: "",
    spec: "",
    unit: "EA",
    qty: "",
    price: "",
    supply: 0,
    vat: 0,
    total: 0,
    remark: "",
  });

  // ----------------------------
  // 🔥 초기 rows (최소 8행 보장)
  // ----------------------------
  const initialRows = useMemo(() => {
    let baseRows = [];

    if (poItems && poItems.length > 0) {
      baseRows = poItems.map((item) => ({
        _id: uuid(),
        code: item.code,
        name: item.name,
        spec: item.spec,
        unit: item.unit,
        qty: item.qty,
        price: item.unit_price || "",
        supply: 0,
        vat: 0,
        total: 0,
        remark: item.remark || "",
      }));
    }

    while (baseRows.length < 8) {
      baseRows.push(makeRow());
    }

    return baseRows;
  }, [poItems]);

  // ----------------------------
  // rows state
  // ----------------------------
  const [rowsData, setRowsData] = useState(initialRows);

  // 🔥 발주서 데이터 기반 자동 적용
  useEffect(() => {
    if (poHeader) {
      setSupplier(poHeader.supplier || "");
      setTitle(`[입고] ${poHeader.title || "발주서"}`);
    }
  }, [poHeader]);

  const updateCell = (id, key, value) =>
    setRowsData((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [key]: value } : r))
    );

  const fmtInputNumber = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const num = Number(v);
    return Number.isNaN(num) ? "" : num.toString();
  };

  // ----------------------------
  // 계산된 rows
  // ----------------------------
  const rows = useMemo(() => {
    return rowsData.map((r) => {
      const qty = Number(r.qty) || 0;
      const price = Number(r.price) || 0;
      const supply = +(qty * price).toFixed(2);
      const vat = +(supply * TAX_RATE).toFixed(2);
      const total = +(supply + vat).toFixed(2);
      return { ...r, supply, vat, total };
    });
  }, [rowsData]);

  const subtotal = useMemo(
    () => rows.reduce((sum, r) => sum + r.supply, 0),
    [rows]
  );
  const vat_total = useMemo(
    () => rows.reduce((sum, r) => sum + r.vat, 0),
    [rows]
  );
  const total = subtotal + vat_total;

  // ----------------------------
  // SUMMARY
  // ----------------------------
  const summaryConfig = {
    title: "입고서 요약",
    sections: [
      { label: "제목", value: title },
      { label: "입고일", value: inDate },
      { label: "공급업체", value: supplier },
      { label: "비고", value: remark || "-" },
      { label: "공급가 합계", value: subtotal.toLocaleString() },
      { label: "세액 합계", value: vat_total.toLocaleString() },
      { label: "총 합계", value: total.toLocaleString() },
    ],
    list: rows.map((r) => ({
      label: `${r.name} (${r.code})`,
      value: `${r.qty} × ${r.price} → ${r.total.toLocaleString()}`,
    })),
  };

  // ----------------------------
  // SAVE BODY
  // ----------------------------
  const getBody = useCallback(() => {
    return {
      values: {
        project: projectId,
        module: "material",
        doc_type: "GR",
        dns,
        title,
        header: {
          title,
          in_date: inDate,
          supplier,
          remark,
          currency: CURRENCY,
          tax_rate: TAX_RATE,
          from_po_dns: fromDns,
        },
        lines: rows.map(({ _id, price, total, item_id, ...rest }) => ({
          ...rest,
          item_id: item_id || null,
          unit_price: Number(price) || 0,
          amount: Number(rest.qty) * Number(price) || 0,
        })),
        subtotal,
        vat_total,
        total,
        summary: summaryConfig,
      },
    };
  }, [
    dns,
    projectId,
    title,
    inDate,
    supplier,
    remark,
    rows,
    subtotal,
    vat_total,
    total,
    fromDns,
  ]);

  // ----------------------------
  // UI
  // ----------------------------
  return (
    <FormContentManager
      params={{
        module: "material",
        doc_type: "GR",
        project: projectId,
        dns,
      }}
      docName="입고서"
      docDescribe="입고 품목의 수량·단가를 입력해 입고서를 작성하세요."
      redirectTo={`/project/${projectId}/material/materialboard`}
      getBody={getBody}
      summaryConfig={summaryConfig}
    >
      {/* 🔥 문서 상단 타이틀 */}
      <div className="text-center mb-6">
        <div className="text-xl font-bold">자재 입고서</div>
      </div>

      {/* HEADER */}
      <div className="overflow-hidden mb-5">
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <TableCell
                label="제목"
                labelWidth="w-24"
                editing
                colSpan={3}
                input={
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                }
              />
            </tr>

            <tr>
              <TableCell
                label="입고일"
                editing
                colSpan={3}
                input={
                  <input
                    type="date"
                    className="border rounded px-2 py-1"
                    value={inDate}
                    onChange={(e) => setInDate(e.target.value)}
                  />
                }
              />
            </tr>

            <tr>
              <TableCell
                label="공급업체"
                editing
                colSpan={3}
                input={
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                  />
                }
              />
            </tr>

            <tr>
              <TableCell
                label="비고 / 메모"
                editing
                colSpan={3}
                input={
                  <textarea
                    className="w-full min-h-[100px] border rounded px-2 py-1"
                    value={remark}
                    onChange={(e) => setremark(e.target.value)}
                  />
                }
              />
            </tr>
          </tbody>
        </table>
      </div>

      {/* ITEM TABLE */}
      <table className="w-full text-sm border table-fixed">
        <colgroup>
          <col /> {/* 코드 */}
          <col /> {/* 품명 */}
          <col /> {/* 규격 */}
          <col style={{ width: "70px" }} /> {/* 단위 */}
          <col style={{ width: "90px" }} /> {/* 수량 */}
          <col style={{ width: "110px" }} /> {/* 단가 */}
          <col style={{ width: "120px" }} /> {/* 공급가 */}
          <col style={{ width: "120px" }} /> {/* 합계 */}
          <col style={{ width: "50px" }} /> {/* 삭제 */}
        </colgroup>

        <thead className="bg-gray-100 border-b text-body text-[13px]">
          <tr>
            <th className="px-2 py-1 border-r">코드</th>
            <th className="px-2 py-1 border-r">품명</th>
            <th className="px-2 py-1 border-r">규격</th>
            <th className="px-2 py-1 border-r text-center">단위</th>
            <th className="px-2 py-1 text-right border-r">수량</th>
            <th className="px-2 py-1 text-right border-r">단가</th>
            <th className="px-2 py-1 text-right border-r">공급가</th>
            <th className="px-2 py-1 text-right border-r">합계</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r) => (
            <tr key={r._id} className="border-t">
              <td className="px-2 py-1 border-r">
                <input
                  className="w-full border rounded px-1"
                  value={r.code}
                  onChange={(e) => updateCell(r._id, "code", e.target.value)}
                />
              </td>

              <td className="px-2 py-1 border-r">
                <input
                  className="w-full border rounded px-1"
                  value={r.name}
                  onChange={(e) => updateCell(r._id, "name", e.target.value)}
                />
              </td>

              <td className="px-2 py-1 border-r">
                <input
                  className="w-full border rounded px-1"
                  value={r.spec}
                  onChange={(e) => updateCell(r._id, "spec", e.target.value)}
                />
              </td>

              <td className="px-2 py-1 border-r text-center">
                <input
                  className="w-full text-center border rounded px-1"
                  value={r.unit}
                  onChange={(e) => updateCell(r._id, "unit", e.target.value)}
                />
              </td>

              <td className="px-2 py-1 text-right border-r">
                <input
                  type="number"
                  className="w-full text-right border rounded px-1"
                  value={fmtInputNumber(r.qty)}
                  onChange={(e) => updateCell(r._id, "qty", e.target.value)}
                />
              </td>

              <td className="px-2 py-1 text-right border-r">
                <input
                  type="number"
                  className="w-full text-right border rounded px-1"
                  value={fmtInputNumber(r.price)}
                  onChange={(e) => updateCell(r._id, "price", e.target.value)}
                />
              </td>

              <td className="px-2 py-1 text-right border-r">
                {Number(r.supply).toLocaleString()}
              </td>

              <td className="px-2 py-1 text-right border-r">
                {Number(r.total).toLocaleString()}
              </td>

              <td className="px-1 py-1 text-center">
                <button
                  className="doc-delete"
                  onClick={() =>
                    setRowsData((prev) => {
                      const filtered = prev.filter((row) => row._id !== r._id);
                      if (filtered.length >= 8) return filtered;

                      const padded = [...filtered];
                      while (padded.length < 8) padded.push(makeRow());
                      return padded;
                    })
                  }
                >
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot className="bg-gray-50 border-t">
          <tr>
            <td colSpan={6} className="px-2 py-2 text-right font-semibold">
              합계
            </td>
            <td className="px-2 py-2 text-right font-semibold">
              {subtotal.toLocaleString()}
            </td>
            <td className="px-2 py-2 text-right font-semibold">
              {total.toLocaleString()}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </FormContentManager>
  );
}
